// src/features/calendar/settings/EventTypesSettings.tsx
// Admin: the calendar event type registry — how products declare their
// calendar-aware entity types (staffing.shift, booking.appointment, …).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useCalendarCalendarEventType, useI18n } from '@react/hooks';
import { Button, Input, Textarea, Badge, Alert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import type { CalendarEventTypeDefinition } from '@core/types/calendar';
import { ModalShell } from '../ModalShell';
import { durationToMinutes, errMessage, makeEntity, minutesToDuration } from '../time';

export const EventTypesSettings = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useCalendarCalendarEventType();

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CalendarEventTypeDefinition | null>(null);
  const [deleting, setDeleting] = useState<CalendarEventTypeDefinition | null>(null);
  const [inspecting, setInspecting] = useState<CalendarEventTypeDefinition | null>(null);

  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [sourceEntityType, setSourceEntityType] = useState('');
  const [defaultDuration, setDefaultDuration] = useState<number | ''>('');
  const [blocksAvailability, setBlocksAvailability] = useState(true);
  const [isBookable, setIsBookable] = useState(false);
  const [enableAttendees, setEnableAttendees] = useState(true);
  const [enableRecurrence, setEnableRecurrence] = useState(true);
  const [enableLocation, setEnableLocation] = useState(true);
  const [enableConference, setEnableConference] = useState(true);
  const [enableReminders, setEnableReminders] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const load = () => {
    setError(null);
    store.ListCalendarEventTypes().catch((e: unknown) => setError(errMessage(e, t('calendar.eventTypes.loadError'))));
  };

  useEffect(() => { load(); }, []);

  const openDialog = (def: CalendarEventTypeDefinition | null) => {
    setEditing(def);
    setSlug(def?.slug ?? '');
    setDisplayName(def?.display_name ?? '');
    setDescription(def?.description ?? '');
    setSourceEntityType(def?.source_entity_type ?? '');
    setDefaultDuration(durationToMinutes(def?.default_duration));
    setBlocksAvailability(def?.blocks_availability ?? true);
    setIsBookable(def?.is_bookable ?? false);
    setEnableAttendees(def?.enable_attendees ?? true);
    setEnableRecurrence(def?.enable_recurrence ?? true);
    setEnableLocation(def?.enable_location ?? true);
    setEnableConference(def?.enable_conference ?? true);
    setEnableReminders(def?.enable_reminders ?? true);
    setDialogOpen(true);
  };

  const inspect = async (def: CalendarEventTypeDefinition) => {
    try {
      const full = await store.GetCalendarEventType(def.entity?.id || def.slug);
      setInspecting(full);
    } catch (e) {
      setError(errMessage(e, t('calendar.eventTypes.loadError')));
    }
  };

  const save = async () => {
    if (!slug.trim() || !displayName.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (editing) {
        await store.UpdateCalendarEventType(editing.entity?.id ?? editing.slug, {
          definition: {
            ...editing,
            display_name: displayName.trim(),
            description,
            default_duration: minutesToDuration(defaultDuration),
            blocks_availability: blocksAvailability,
            is_bookable: isBookable,
            enable_attendees: enableAttendees,
            enable_recurrence: enableRecurrence,
            enable_location: enableLocation,
            enable_conference: enableConference,
            enable_reminders: enableReminders,
          },
          field_mask: [
            'display_name', 'description', 'default_duration', 'blocks_availability', 'is_bookable',
            'enable_attendees', 'enable_recurrence', 'enable_location', 'enable_conference', 'enable_reminders',
          ],
        });
      } else {
        await store.RegisterCalendarEventType({
          entity: makeEntity('', 'calendar_event_type_definition', scope),
          slug: slug.trim(),
          display_name: displayName.trim(),
          description: description || undefined,
          product_id: scope.productId,
          source_entity_type: sourceEntityType.trim(),
          default_duration: minutesToDuration(defaultDuration),
          default_reminders: [],
          blocks_availability: blocksAvailability,
          is_bookable: isBookable,
          enable_attendees: enableAttendees,
          enable_recurrence: enableRecurrence,
          enable_location: enableLocation,
          enable_conference: enableConference,
          enable_reminders: enableReminders,
          field_mapping: {},
        });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('calendar.eventTypes.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  const flag = (label: string, value: boolean, set: (v: boolean) => void) => (
    <label className="flex items-center gap-2 text-sm text-foreground">
      <input type="checkbox" checked={value} onChange={(e) => set(e.target.checked)} />
      {label}
    </label>
  );

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('calendar.eventTypes.hint')}</p>
        <Button size="sm" onClick={() => openDialog(null)}>
          {t('calendar.eventTypes.register')}
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {store.isLoading && store.items.length === 0 && (
        <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />
      )}

      {!store.isLoading && store.items.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('calendar.eventTypes.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((def) => (
          <div key={def.entity?.id ?? def.slug} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{def.display_name}</span>
                <span className="font-mono text-xs text-muted-foreground">{def.slug}</span>
                {!def.is_active && <Badge variant="warning">{t('calendar.eventTypes.inactive')}</Badge>}
                {def.is_bookable && <Badge variant="secondary">{t('calendar.eventTypes.bookable')}</Badge>}
              </div>
              {def.description && <p className="mt-0.5 truncate text-sm text-muted-foreground">{def.description}</p>}
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => inspect(def)}>
                {t('common.open')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openDialog(def)}>
                {t('common.edit')}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(def)}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Register / edit */}
      <ModalShell
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('calendar.eventTypes.edit') : t('calendar.eventTypes.register')}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={save} isLoading={isSaving} disabled={!slug.trim() || !displayName.trim()}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input
              label={t('calendar.eventTypes.slug')}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="staffing.shift"
              disabled={!!editing}
              required
            />
            <Input label={t('calendar.eventTypes.displayName')} value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </div>
          <Textarea label={t('common.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input
              label={t('calendar.eventTypes.sourceEntityType')}
              value={sourceEntityType}
              onChange={(e) => setSourceEntityType(e.target.value)}
              placeholder="shift"
              disabled={!!editing}
            />
            <Input
              label={t('calendar.eventTypes.defaultDurationMin')}
              type="text"
              inputMode="numeric"
              value={defaultDuration === '' ? '' : String(defaultDuration)}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, '');
                setDefaultDuration(v === '' ? '' : parseInt(v, 10));
              }}
            />
          </div>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {flag(t('calendar.eventTypes.blocksAvailability'), blocksAvailability, setBlocksAvailability)}
            {flag(t('calendar.eventTypes.isBookable'), isBookable, setIsBookable)}
            {flag(t('calendar.eventTypes.enableAttendees'), enableAttendees, setEnableAttendees)}
            {flag(t('calendar.eventTypes.enableRecurrence'), enableRecurrence, setEnableRecurrence)}
            {flag(t('calendar.eventTypes.enableLocation'), enableLocation, setEnableLocation)}
            {flag(t('calendar.eventTypes.enableConference'), enableConference, setEnableConference)}
            {flag(t('calendar.eventTypes.enableReminders'), enableReminders, setEnableReminders)}
          </div>
        </div>
      </ModalShell>

      {/* Inspect */}
      <ModalShell
        open={!!inspecting}
        onClose={() => setInspecting(null)}
        title={inspecting?.display_name ?? ''}
      >
        {inspecting && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">{t('calendar.eventTypes.slug')}</dt>
            <dd className="font-mono text-foreground">{inspecting.slug}</dd>
            <dt className="text-muted-foreground">{t('calendar.eventTypes.sourceEntityType')}</dt>
            <dd className="text-foreground">{inspecting.source_entity_type || '—'}</dd>
            <dt className="text-muted-foreground">{t('calendar.eventTypes.defaultDurationMin')}</dt>
            <dd className="text-foreground">{durationToMinutes(inspecting.default_duration) || '—'}</dd>
            <dt className="text-muted-foreground">{t('calendar.eventTypes.blocksAvailability')}</dt>
            <dd className="text-foreground">{inspecting.blocks_availability ? t('common.yes') : t('common.no')}</dd>
            <dt className="text-muted-foreground">{t('calendar.eventTypes.isBookable')}</dt>
            <dd className="text-foreground">{inspecting.is_bookable ? t('common.yes') : t('common.no')}</dd>
            <dt className="text-muted-foreground">{t('calendar.eventTypes.sourceTopics')}</dt>
            <dd className="font-mono text-xs text-foreground">
              {[inspecting.source_created_topic, inspecting.source_updated_topic, inspecting.source_deleted_topic].filter(Boolean).join(', ') || '—'}
            </dd>
          </dl>
        )}
      </ModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('calendar.eventTypes.deleteTitle')}
        description={t('calendar.eventTypes.deleteDescription')}
        destructive
        isConfirming={store.isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await store.DeleteCalendarEventType(deleting.entity?.id ?? deleting.slug);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('calendar.eventTypes.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
